import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { api } from '../../api/client';
import './AdminPanel.css';

/* ══════════════════════════════════════════════════════
   HELPERS
   ══════════════════════════════════════════════════════ */
const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const fmtDateTime = (d) =>
  d ? new Date(d).toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  }) : '—';

const StatusPill = ({ status }) => (
  <span className={`adm-pill adm-pill--${(status || 'unknown').toLowerCase()}`}>
    {status || 'unknown'}
  </span>
);

const Stars = ({ rating = 0 }) => (
  <span className="adm-stars">
    {[1, 2, 3, 4, 5].map((n) => (
      <span key={n} className={n <= rating ? 'adm-star adm-star--on' : 'adm-star'}>★</span>
    ))}
  </span>
);

/* ══════════════════════════════════════════════════════
   ADMIN PANEL
   ══════════════════════════════════════════════════════ */
export default function AdminPanel() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [tab, setTab] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(false);

  /* ── Live role verification ── */
  const [roleChecked, setRoleChecked] = useState(false);
  const [liveRole, setLiveRole] = useState(null);

  useEffect(() => {
    api.getProfile()
      .then((data) => {
        const role = data?.user?.role;
        setLiveRole(role);
        try {
          const stored = JSON.parse(localStorage.getItem('user') || '{}');
          localStorage.setItem('user', JSON.stringify({ ...stored, role }));
        } catch {}
      })
      .catch(() => setLiveRole(user?.role || null))
      .finally(() => setRoleChecked(true));
  }, []); // eslint-disable-line

  /* ── Data state ── */
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [memories, setMemories] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({ msg: '', type: 'success' });

  /* ── Search / Filter ── */
  const [userSearch, setUserSearch] = useState('');
  const [bookingFilter, setBookingFilter] = useState('all');
  const [reviewFilter, setReviewFilter] = useState('all');
  const [memoryFilter, setMemoryFilter] = useState('all');
  const [msgFilter, setMsgFilter] = useState('all');

  /* ── Expanded / lightbox ── */
  const [expandedReview, setExpandedReview] = useState(null);
  const [expandedMemory, setExpandedMemory] = useState(null);

  /* ── Guest profile modal ── */
  const [selectedBooking, setSelectedBooking] = useState(null);

  /* ── Toast ── */
  const toastTimer = useRef(null);
  const showToast = (msg, type = 'success') => {
    clearTimeout(toastTimer.current);
    setToast({ msg, type });
    toastTimer.current = setTimeout(() => setToast({ msg: '', type: 'success' }), 3500);
  };

  /* ── Loaders ── */
  const loadDashboard = useCallback(async () => {
    try { setStats(await api.adminDashboard()); } catch { /* ignore */ }
  }, []);
  const loadUsers = useCallback(async () => {
    try { setUsers(await api.adminGetUsers()); } catch { /* ignore */ }
  }, []);
  const loadBookings = useCallback(async () => {
    try { setBookings(await api.adminGetBookings()); } catch { /* ignore */ }
  }, []);
  const loadReviews = useCallback(async () => {
    try { setReviews(await api.adminGetReviews()); } catch { /* ignore */ }
  }, []);
  const loadMemories = useCallback(async () => {
    try { setMemories(await api.adminGetMemories()); } catch { /* ignore */ }
  }, []);
  const loadMessages = useCallback(async () => {
    try { setMessages(await api.adminGetMessages()); } catch { /* ignore */ }
  }, []);

  /* Load tab data after role confirmed */
  useEffect(() => {
    if (!roleChecked || liveRole !== 'admin') return;
    setLoading(true);
    const loaders = {
      dashboard: () => Promise.all([loadDashboard(), loadBookings()]),
      users: loadUsers,
      bookings: loadBookings,
      reviews: loadReviews,
      memories: loadMemories,
      messages: loadMessages,
    };
    (loaders[tab] || loadDashboard)().finally(() => setLoading(false));
  }, [tab, roleChecked, liveRole, loadDashboard, loadUsers, loadBookings, loadReviews, loadMemories, loadMessages]);

  /* Always load dashboard in background for sidebar badge counts */
  useEffect(() => {
    if (roleChecked && liveRole === 'admin' && !stats) loadDashboard();
  }, [roleChecked, liveRole, stats, loadDashboard]);

  /* ── Sidebar badge counts ── */
  const badges = useMemo(() => ({
    bookings: stats?.pendingBookings || 0,
    reviews: stats?.pendingReviews || 0,
    memories: stats?.pendingMemories || 0,
    messages: stats?.unreadMessages || 0,
  }), [stats]);

  /* ── Filtered lists (must be before any early returns) ── */
  const filteredUsers = useMemo(() => {
    const q = userSearch.toLowerCase();
    return users.filter((u) =>
      !q || u.full_name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q)
    );
  }, [users, userSearch]);

  const filteredBookings = useMemo(() =>
    bookingFilter === 'all' ? bookings : bookings.filter((b) => b.status === bookingFilter),
  [bookings, bookingFilter]);

  const filteredReviews = useMemo(() =>
    reviewFilter === 'all' ? reviews : reviews.filter((r) => r.status === reviewFilter),
  [reviews, reviewFilter]);

  const filteredMemories = useMemo(() =>
    memoryFilter === 'all' ? memories : memories.filter((m) => m.status === memoryFilter),
  [memories, memoryFilter]);

  const filteredMessages = useMemo(() =>
    msgFilter === 'all' ? messages : messages.filter((m) => msgFilter === 'unread' ? !m.is_read : m.is_read),
  [messages, msgFilter]);

  /* ══════════════════════════════
     GUARDS
     ══════════════════════════════ */
  if (!user) return (
    <div className="adm adm--center">
      <div className="adm-denied">
        <div className="adm-denied__icon">🔒</div>
        <h2>Access Denied</h2>
        <p>You must be logged in to access this panel.</p>
      </div>
    </div>
  );

  if (!roleChecked) return (
    <div className="adm adm--center">
      <div className="adm-spinner-wrap">
        <div className="adm-spinner" />
        <p>Verifying access…</p>
      </div>
    </div>
  );

  if (liveRole !== 'admin') return (
    <div className="adm adm--center">
      <div className="adm-denied">
        <div className="adm-denied__icon">🚫</div>
        <h2>Access Denied</h2>
        <p>You do not have admin privileges.</p>
        <p className="adm-denied__hint">
          Make sure your role has been updated in the database and the server has been restarted.
        </p>
      </div>
    </div>
  );

  /* ══════════════════════════════
     ACTIONS
     ══════════════════════════════ */
  const handleRoleChange = async (id, newRole) => {
    try {
      await api.adminUpdateUserRole(id, newRole);
      showToast(`✅ Role updated to ${newRole}`);
      loadUsers();
    } catch { showToast('❌ Failed to update role', 'error'); }
  };

  const handleDeleteUser = async (id, name) => {
    if (!window.confirm(`Delete ${name || 'this user'} and all their data permanently?`)) return;
    try { await api.adminDeleteUser(id); showToast('🗑️ User deleted'); loadUsers(); }
    catch { showToast('❌ Failed to delete user', 'error'); }
  };

  const handleBookingStatus = async (id, status) => {
    try {
      await api.adminUpdateBookingStatus(id, status);
      showToast(`✅ Booking ${status}`);
      loadBookings();
      loadDashboard();
    } catch { showToast('❌ Failed to update booking', 'error'); }
  };

  const handleDeleteBooking = async (id) => {
    if (!window.confirm('Delete this booking permanently?')) return;
    try { await api.adminDeleteBooking(id); showToast('🗑️ Booking deleted'); loadBookings(); loadDashboard(); }
    catch { showToast('❌ Failed', 'error'); }
  };

  const handleReviewStatus = async (id, status) => {
    try {
      await api.adminUpdateReviewStatus(id, status);
      showToast(`✅ Review ${status}`);
      loadReviews();
      loadDashboard();
    } catch { showToast('❌ Failed to update review', 'error'); }
  };

  const handleDeleteReview = async (id) => {
    if (!window.confirm('Delete this review permanently?')) return;
    try { await api.adminDeleteReview(id); showToast('🗑️ Review deleted'); loadReviews(); loadDashboard(); }
    catch { showToast('❌ Failed', 'error'); }
  };

  const handleMemoryStatus = async (id, status) => {
    try {
      await api.adminUpdateMemoryStatus(id, status);
      showToast(`✅ Memory ${status}`);
      loadMemories();
      loadDashboard();
    } catch { showToast('❌ Failed', 'error'); }
  };

  const handleDeleteMemory = async (id) => {
    if (!window.confirm('Delete this memory permanently?')) return;
    try { await api.adminDeleteMemory(id); showToast('🗑️ Memory deleted'); loadMemories(); loadDashboard(); }
    catch { showToast('❌ Failed', 'error'); }
  };

  const handleMarkRead = async (id) => {
    try { await api.adminMarkMessageRead(id); loadMessages(); loadDashboard(); }
    catch { /* ignore */ }
  };

  const handleDeleteMessage = async (id) => {
    if (!window.confirm('Delete this message permanently?')) return;
    try { await api.adminDeleteMessage(id); showToast('🗑️ Message deleted'); loadMessages(); loadDashboard(); }
    catch { showToast('❌ Failed', 'error'); }
  };

  /* ══════════════════════════════
     TAB DEFINITIONS
     ══════════════════════════════ */
  const TABS = [
    { key: 'dashboard', icon: '📊', label: 'Dashboard' },
    { key: 'users',     icon: '👥', label: 'Users' },
    { key: 'bookings',  icon: '📅', label: 'Bookings' },
    { key: 'reviews',   icon: '⭐', label: 'Reviews' },
    { key: 'memories',  icon: '📸', label: 'Memories' },
    { key: 'messages',  icon: '✉️', label: 'Messages' },
  ];

  /* ══════════════════════════════
     RENDER — DASHBOARD
     ══════════════════════════════ */
  const renderDashboard = () => {
    if (!stats) return <div className="adm-empty">No data yet.</div>;

    const topCards = [
      { label: 'Total Users',      value: stats.users,           icon: '👥', color: 'blue',   sub: 'Registered accounts' },
      { label: 'Total Bookings',   value: stats.bookings,        icon: '📅', color: 'green',  sub: `${stats.pendingBookings} pending` },
      { label: 'Total Reviews',    value: stats.reviews,         icon: '⭐', color: 'yellow', sub: `${stats.pendingReviews} awaiting approval` },
      { label: 'Contact Messages', value: stats.messages,        icon: '✉️', color: 'purple', sub: `${stats.unreadMessages} unread` },
    ];

    const recentBookings = [...bookings]
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
      .slice(0, 6);

    return (
      <div className="adm-dashboard">
        <div className="adm-section-header">
          <h2 className="adm-section-title">Dashboard Overview</h2>
          <button
            className="adm-btn adm-btn--ghost"
            onClick={() => {
              setLoading(true);
              Promise.all([loadDashboard(), loadBookings()]).finally(() => setLoading(false));
            }}
          >
            ↻ Refresh
          </button>
        </div>

        {/* KPI metric cards */}
        <div className="adm-metrics">
          {topCards.map((c, i) => (
            <div className={`adm-metric adm-metric--${c.color}`} key={i}>
              <div className="adm-metric__icon">{c.icon}</div>
              <div className="adm-metric__body">
                <div className="adm-metric__value">{c.value ?? '—'}</div>
                <div className="adm-metric__label">{c.label}</div>
                <div className="adm-metric__sub">{c.sub}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Two-column bottom section */}
        <div className="adm-dash-bottom">

          {/* Pending actions */}
          <div className="adm-dash-col">
            <h3 className="adm-subsection-title">⚡ Pending Actions</h3>
            <div className="adm-status-grid">
              {[
                { label: 'Pending Bookings',   value: stats.pendingBookings,   icon: '⏳', tab: 'bookings',  urgent: stats.pendingBookings > 0 },
                { label: 'Confirmed Bookings', value: stats.confirmedBookings, icon: '✅', tab: 'bookings',  urgent: false },
                { label: 'Pending Reviews',    value: stats.pendingReviews,    icon: '📝', tab: 'reviews',   urgent: stats.pendingReviews > 0 },
                { label: 'Pending Memories',   value: stats.pendingMemories,   icon: '🖼️', tab: 'memories',  urgent: stats.pendingMemories > 0 },
                { label: 'Unread Messages',    value: stats.unreadMessages,    icon: '🔔', tab: 'messages',  urgent: stats.unreadMessages > 0 },
                { label: 'Total Memories',     value: stats.memories,          icon: '📸', tab: 'memories',  urgent: false },
              ].map((c, i) => (
                <button
                  key={i}
                  className={`adm-status-card${c.urgent ? ' adm-status-card--urgent' : ''}`}
                  onClick={() => setTab(c.tab)}
                  title={`Go to ${c.tab}`}
                >
                  <span className="adm-status-card__icon">{c.icon}</span>
                  <span className="adm-status-card__value">{c.value ?? '—'}</span>
                  <span className="adm-status-card__label">{c.label}</span>
                </button>
              ))}
            </div>

            <h3 className="adm-subsection-title" style={{ marginTop: '28px' }}>🚀 Quick Actions</h3>
            <div className="adm-quick-actions">
              <button className="adm-quick-btn" onClick={() => setTab('bookings')}>
                📅 Review Bookings
                {stats.pendingBookings > 0 && <span className="adm-badge">{stats.pendingBookings}</span>}
              </button>
              <button className="adm-quick-btn" onClick={() => setTab('reviews')}>
                ⭐ Approve Reviews
                {stats.pendingReviews > 0 && <span className="adm-badge">{stats.pendingReviews}</span>}
              </button>
              <button className="adm-quick-btn" onClick={() => setTab('memories')}>
                📸 Approve Memories
                {stats.pendingMemories > 0 && <span className="adm-badge">{stats.pendingMemories}</span>}
              </button>
              <button className="adm-quick-btn" onClick={() => setTab('messages')}>
                ✉️ Read Messages
                {stats.unreadMessages > 0 && <span className="adm-badge">{stats.unreadMessages}</span>}
              </button>
            </div>
          </div>

          {/* Recent bookings feed */}
          <div className="adm-dash-col">
            <h3 className="adm-subsection-title">📋 Recent Bookings</h3>
            {recentBookings.length === 0 ? (
              <div className="adm-empty" style={{ fontSize: '0.9rem' }}>No bookings yet.</div>
            ) : (
              <div className="adm-recent-list">
                {recentBookings.map((b) => (
                  <button
                    key={b.id}
                    className="adm-recent-item"
                    onClick={() => setSelectedBooking(b)}
                  >
                    <div className="adm-recent-item__avatar">
                      {(b.full_name?.[0] || b.guest_name?.[0] || '?').toUpperCase()}
                    </div>
                    <div className="adm-recent-item__info">
                      <div className="adm-recent-item__name">
                        {b.full_name || b.guest_name || '—'}
                      </div>
                      <div className="adm-recent-item__dates">
                        {fmtDate(b.check_in)} → {fmtDate(b.check_out)}
                        {b.phone && <span className="adm-recent-item__phone">📞 {b.phone}</span>}
                      </div>
                    </div>
                    <StatusPill status={b.status} />
                  </button>
                ))}
                <button
                  className="adm-btn adm-btn--ghost"
                  style={{ width: '100%', marginTop: '10px', justifyContent: 'center' }}
                  onClick={() => setTab('bookings')}
                >
                  View all bookings →
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  /* ══════════════════════════════
     RENDER — USERS
     ══════════════════════════════ */
  const renderUsers = () => (
    <div className="adm-section">
      <div className="adm-section-header">
        <h2 className="adm-section-title">
          All Users <span className="adm-count">({filteredUsers.length})</span>
        </h2>
        <div className="adm-toolbar">
          <div className="adm-search-wrap">
            <span className="adm-search-icon">🔍</span>
            <input
              className="adm-search"
              placeholder="Search name or email…"
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
            />
            {userSearch && (
              <button className="adm-search-clear" onClick={() => setUserSearch('')}>✕</button>
            )}
          </div>
        </div>
      </div>

      <div className="adm-table-wrap">
        <table className="adm-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Joined</th>
              <th>Bookings</th>
              <th>Reviews</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map((u) => (
              <tr key={u.id} className={u.role === 'admin' ? 'adm-table__row--admin' : ''}>
                <td className="adm-table__id">#{u.id}</td>
                <td>
                  <div className="adm-user-cell">
                    <div className="adm-user-avatar">
                      {(u.full_name?.[0] || u.email?.[0] || '?').toUpperCase()}
                    </div>
                    <span>{u.full_name || '—'}</span>
                  </div>
                </td>
                <td className="adm-table__muted">{u.email}</td>
                <td>
                  <select
                    className="adm-select"
                    value={u.role || 'user'}
                    onChange={(e) => handleRoleChange(u.id, e.target.value)}
                  >
                    <option value="user">user</option>
                    <option value="admin">admin</option>
                  </select>
                </td>
                <td className="adm-table__muted">{fmtDate(u.created_at)}</td>
                <td className="adm-table__center">{u.booking_count ?? 0}</td>
                <td className="adm-table__center">{u.review_count ?? 0}</td>
                <td>
                  <button
                    className="adm-icon-btn adm-icon-btn--danger"
                    title="Delete user"
                    onClick={() => handleDeleteUser(u.id, u.full_name)}
                  >
                    🗑️
                  </button>
                </td>
              </tr>
            ))}
            {filteredUsers.length === 0 && (
              <tr>
                <td colSpan="8" className="adm-table__empty">
                  No users found{userSearch && ` for "${userSearch}"`}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  /* ══════════════════════════════
     RENDER — BOOKINGS
     ══════════════════════════════ */
  const renderBookings = () => (
    <div className="adm-section">
      <div className="adm-section-header">
        <h2 className="adm-section-title">
          All Bookings <span className="adm-count">({filteredBookings.length})</span>
        </h2>
        <div className="adm-filter-tabs">
          {['all', 'pending', 'confirmed', 'cancelled', 'completed'].map((s) => (
            <button
              key={s}
              className={`adm-filter-tab${bookingFilter === s ? ' adm-filter-tab--active' : ''}`}
              onClick={() => setBookingFilter(s)}
            >
              {s.charAt(0).toUpperCase() + s.slice(1)}
              {s !== 'all' && (
                <span className="adm-filter-count">
                  {bookings.filter((b) => b.status === s).length}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      <div className="adm-table-wrap">
        <table className="adm-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Guest</th>
              <th>Email / Phone</th>
              <th>Check-in</th>
              <th>Check-out</th>
              <th>Guests</th>
              <th>Status</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredBookings.map((b) => (
              <tr key={b.id}>
                <td className="adm-table__id">#{b.id}</td>
                <td><strong>{b.guest_name || b.full_name || '—'}</strong></td>
                <td className="adm-table__muted">
                  <div>{b.email || '—'}</div>
                  {b.phone && <div className="adm-table__sub">{b.phone}</div>}
                </td>
                <td>{fmtDate(b.check_in)}</td>
                <td>{fmtDate(b.check_out)}</td>
                <td className="adm-table__center">{b.guests ?? '—'}</td>
                <td><StatusPill status={b.status} /></td>
                <td className="adm-table__muted">{fmtDate(b.created_at)}</td>
                <td>
                  <div className="adm-action-group">
                    <button
                      className="adm-icon-btn adm-icon-btn--info"
                      title="View guest profile"
                      onClick={() => setSelectedBooking(b)}
                    >👤</button>
                    {b.status === 'pending' && (
                      <>
                        <button
                          className="adm-icon-btn adm-icon-btn--success"
                          title="Confirm"
                          onClick={() => handleBookingStatus(b.id, 'confirmed')}
                        >✅</button>
                        <button
                          className="adm-icon-btn adm-icon-btn--warn"
                          title="Cancel"
                          onClick={() => handleBookingStatus(b.id, 'cancelled')}
                        >❌</button>
                      </>
                    )}
                    {b.status === 'confirmed' && (
                      <>
                        <button
                          className="adm-icon-btn adm-icon-btn--success"
                          title="Mark Completed"
                          onClick={() => handleBookingStatus(b.id, 'completed')}
                        >🏁</button>
                        <button
                          className="adm-icon-btn adm-icon-btn--warn"
                          title="Cancel"
                          onClick={() => handleBookingStatus(b.id, 'cancelled')}
                        >❌</button>
                      </>
                    )}
                    <button
                      className="adm-icon-btn adm-icon-btn--danger"
                      title="Delete"
                      onClick={() => handleDeleteBooking(b.id)}
                    >🗑️</button>
                  </div>
                </td>
              </tr>
            ))}
            {filteredBookings.length === 0 && (
              <tr>
                <td colSpan="9" className="adm-table__empty">
                  No {bookingFilter !== 'all' ? bookingFilter : ''} bookings found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  /* ══════════════════════════════
     RENDER — REVIEWS
     ══════════════════════════════ */
  const renderReviews = () => (
    <div className="adm-section">
      <div className="adm-section-header">
        <h2 className="adm-section-title">
          All Reviews <span className="adm-count">({filteredReviews.length})</span>
        </h2>
        <div className="adm-filter-tabs">
          {['all', 'pending', 'approved', 'rejected'].map((s) => (
            <button
              key={s}
              className={`adm-filter-tab${reviewFilter === s ? ' adm-filter-tab--active' : ''}`}
              onClick={() => setReviewFilter(s)}
            >
              {s.charAt(0).toUpperCase() + s.slice(1)}
              {s !== 'all' && (
                <span className="adm-filter-count">
                  {reviews.filter((r) => r.status === s).length}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      <div className="adm-review-list">
        {filteredReviews.map((r) => (
          <div className={`adm-review${r.status === 'pending' ? ' adm-review--pending' : ''}`} key={r.id}>
            {r.status === 'pending' && (
              <div className="adm-review__pending-badge">Awaiting Approval</div>
            )}
            <div className="adm-review__head">
              <div className="adm-review__author">
                <div className="adm-user-avatar adm-user-avatar--sm">
                  {(r.full_name?.[0] || '?').toUpperCase()}
                </div>
                <div>
                  <div className="adm-review__name">{r.full_name || 'Anonymous'}</div>
                  <div className="adm-review__meta">{r.email} · {fmtDateTime(r.created_at)}</div>
                </div>
              </div>
              <div className="adm-review__right">
                <Stars rating={r.rating} />
                <StatusPill status={r.status} />
              </div>
            </div>

            {r.title && <h4 className="adm-review__title">"{r.title}"</h4>}
            <p className={`adm-review__body${expandedReview === r.id ? ' adm-review__body--expanded' : ''}`}>
              {r.body || r.comment || '—'}
            </p>
            {(r.body || r.comment || '').length > 200 && (
              <button
                className="adm-review__toggle"
                onClick={() => setExpandedReview(expandedReview === r.id ? null : r.id)}
              >
                {expandedReview === r.id ? '▲ Show less' : '▼ Read more'}
              </button>
            )}

            <div className="adm-review__tags">
              {r.visit_season && <span className="adm-tag">🗓️ {r.visit_season}</span>}
              {r.travel_type  && <span className="adm-tag">✈️ {r.travel_type}</span>}
              {r.would_return && <span className="adm-tag adm-tag--green">↩️ Would return</span>}
            </div>

            <div className="adm-review__actions">
              {r.status !== 'approved' && (
                <button className="adm-btn adm-btn--success" onClick={() => handleReviewStatus(r.id, 'approved')}>
                  ✅ Approve
                </button>
              )}
              {r.status !== 'rejected' && (
                <button className="adm-btn adm-btn--warn" onClick={() => handleReviewStatus(r.id, 'rejected')}>
                  ❌ Reject
                </button>
              )}
              {r.status === 'rejected' && (
                <button className="adm-btn adm-btn--ghost" onClick={() => handleReviewStatus(r.id, 'pending')}>
                  ↩ Set Pending
                </button>
              )}
              <button className="adm-btn adm-btn--danger" onClick={() => handleDeleteReview(r.id)}>
                🗑️ Delete
              </button>
            </div>
          </div>
        ))}
        {filteredReviews.length === 0 && (
          <div className="adm-empty">No {reviewFilter !== 'all' ? reviewFilter : ''} reviews found.</div>
        )}
      </div>
    </div>
  );

  /* ══════════════════════════════
     RENDER — MEMORIES
     ══════════════════════════════ */
  const renderMemories = () => (
    <div className="adm-section">
      <div className="adm-section-header">
        <h2 className="adm-section-title">
          User Memories <span className="adm-count">({filteredMemories.length})</span>
        </h2>
        <div className="adm-filter-tabs">
          {['all', 'pending', 'approved', 'rejected'].map((s) => (
            <button
              key={s}
              className={`adm-filter-tab${memoryFilter === s ? ' adm-filter-tab--active' : ''}`}
              onClick={() => setMemoryFilter(s)}
            >
              {s.charAt(0).toUpperCase() + s.slice(1)}
              {s !== 'all' && (
                <span className="adm-filter-count">
                  {memories.filter((m) => m.status === s).length}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      <div className="adm-memory-grid">
        {filteredMemories.map((m) => (
          <div className={`adm-memory${m.status === 'pending' ? ' adm-memory--pending' : ''}`} key={m.id}>
            <div
              className="adm-memory__img-wrap"
              onClick={() => setExpandedMemory(expandedMemory === m.id ? null : m.id)}
            >
              <img src={m.url} alt={m.caption || ''} loading="lazy" />
              {m.status === 'pending' && <div className="adm-memory__overlay">Pending</div>}
            </div>
            <div className="adm-memory__body">
              <div className="adm-memory__meta-row">
                <div className="adm-user-avatar adm-user-avatar--xs">
                  {(m.full_name?.[0] || '?').toUpperCase()}
                </div>
                <div>
                  <div className="adm-memory__author">{m.full_name || 'Unknown'}</div>
                  <div className="adm-memory__date">{fmtDate(m.created_at)}</div>
                </div>
                <StatusPill status={m.status} />
              </div>
              {m.caption && <p className="adm-memory__caption">"{m.caption}"</p>}
              <div className="adm-memory__tags">
                {m.category && <span className="adm-tag">🏷️ {m.category}</span>}
                <span className="adm-tag">❤️ {m.likes_count ?? 0}</span>
              </div>
              <div className="adm-memory__actions">
                {m.status !== 'approved' && (
                  <button
                    className="adm-btn adm-btn--success adm-btn--sm"
                    onClick={() => handleMemoryStatus(m.id, 'approved')}
                  >✅ Approve</button>
                )}
                {m.status !== 'rejected' && (
                  <button
                    className="adm-btn adm-btn--warn adm-btn--sm"
                    onClick={() => handleMemoryStatus(m.id, 'rejected')}
                  >❌ Reject</button>
                )}
                <button
                  className="adm-btn adm-btn--danger adm-btn--sm"
                  onClick={() => handleDeleteMemory(m.id)}
                >🗑️</button>
              </div>
            </div>
          </div>
        ))}
        {filteredMemories.length === 0 && (
          <div className="adm-empty">No {memoryFilter !== 'all' ? memoryFilter : ''} memories found.</div>
        )}
      </div>

      {/* Lightbox */}
      {expandedMemory && (() => {
        const m = memories.find((x) => x.id === expandedMemory);
        if (!m) return null;
        return (
          <div className="adm-lightbox" onClick={() => setExpandedMemory(null)}>
            <div className="adm-lightbox__inner" onClick={(e) => e.stopPropagation()}>
              <img src={m.url} alt={m.caption || ''} />
              <div className="adm-lightbox__info">
                <p>{m.caption || 'No caption'}</p>
                <p className="adm-table__muted">By {m.full_name} · {fmtDate(m.created_at)}</p>
              </div>
              <button className="adm-lightbox__close" onClick={() => setExpandedMemory(null)}>✕</button>
            </div>
          </div>
        );
      })()}
    </div>
  );

  /* ══════════════════════════════
     RENDER — MESSAGES
     ══════════════════════════════ */
  const renderMessages = () => (
    <div className="adm-section">
      <div className="adm-section-header">
        <h2 className="adm-section-title">
          Contact Messages <span className="adm-count">({filteredMessages.length})</span>
        </h2>
        <div className="adm-filter-tabs">
          {[['all', 'All'], ['unread', 'Unread'], ['read', 'Read']].map(([key, label]) => (
            <button
              key={key}
              className={`adm-filter-tab${msgFilter === key ? ' adm-filter-tab--active' : ''}`}
              onClick={() => setMsgFilter(key)}
            >
              {label}
              {key === 'unread' && (
                <span className="adm-filter-count">{messages.filter((m) => !m.is_read).length}</span>
              )}
            </button>
          ))}
        </div>
      </div>

      <div className="adm-msg-list">
        {filteredMessages.map((m) => (
          <div className={`adm-msg${!m.is_read ? ' adm-msg--unread' : ''}`} key={m.id}>
            {!m.is_read && <div className="adm-msg__dot" />}
            <div className="adm-msg__head">
              <div className="adm-msg__from">
                <strong>{m.name || 'Unknown'}</strong>
                <a href={`mailto:${m.email}`} className="adm-msg__email">{m.email}</a>
              </div>
              <div className="adm-msg__when">{fmtDateTime(m.created_at)}</div>
            </div>
            {m.subject && <div className="adm-msg__subject">{m.subject}</div>}
            <p className="adm-msg__body">{m.message}</p>
            <div className="adm-msg__actions">
              {!m.is_read && (
                <button className="adm-btn adm-btn--ghost" onClick={() => handleMarkRead(m.id)}>
                  ✅ Mark as read
                </button>
              )}
              <a
                href={`mailto:${m.email}?subject=Re: ${m.subject || 'Your message'}`}
                className="adm-btn adm-btn--ghost"
              >
                ✉️ Reply
              </a>
              <button className="adm-btn adm-btn--danger" onClick={() => handleDeleteMessage(m.id)}>
                🗑️ Delete
              </button>
            </div>
          </div>
        ))}
        {filteredMessages.length === 0 && (
          <div className="adm-empty">No {msgFilter !== 'all' ? msgFilter : ''} messages found.</div>
        )}
      </div>
    </div>
  );

  /* ══════════════════════════════
     MAIN RENDER
     ══════════════════════════════ */
  const renderContent = () => {
    if (loading) return (
      <div className="adm-spinner-wrap">
        <div className="adm-spinner" />
        <p>Loading…</p>
      </div>
    );
    switch (tab) {
      case 'dashboard': return renderDashboard();
      case 'users':     return renderUsers();
      case 'bookings':  return renderBookings();
      case 'reviews':   return renderReviews();
      case 'memories':  return renderMemories();
      case 'messages':  return renderMessages();
      default:          return null;
    }
  };

  return (
    <div className="adm">
      {/* ═══ SIDEBAR ═══ */}
      <aside className={`adm-sidebar${sidebarOpen ? ' adm-sidebar--open' : ''}`}>
        <div className="adm-sidebar__brand">
          <span className="adm-sidebar__logo">🌲</span>
          <div>
            <div className="adm-sidebar__title">Admin Panel</div>
            <div className="adm-sidebar__sub">Cabana Piatra Buru</div>
          </div>
        </div>

        <nav className="adm-sidebar__nav">
          {TABS.map(({ key, icon, label }) => (
            <button
              key={key}
              className={`adm-sidebar__link${tab === key ? ' adm-sidebar__link--active' : ''}`}
              onClick={() => { setTab(key); setSidebarOpen(false); }}
            >
              <span className="adm-sidebar__link-icon">{icon}</span>
              <span className="adm-sidebar__link-label">{label}</span>
              {badges[key] > 0 && (
                <span className="adm-sidebar__badge">{badges[key]}</span>
              )}
            </button>
          ))}
        </nav>

        <div className="adm-sidebar__footer">
          <div className="adm-sidebar__avatar">
            {(user?.full_name?.[0] || '?').toUpperCase()}
          </div>
          <div className="adm-sidebar__footer-info">
            <div className="adm-sidebar__user">{user?.full_name || 'Admin'}</div>
            <div className="adm-sidebar__role">Administrator</div>
          </div>
        </div>
      </aside>

      {sidebarOpen && <div className="adm-backdrop" onClick={() => setSidebarOpen(false)} />}

      {/* ═══ MAIN ═══ */}
      <main className="adm-main">
        <header className="adm-topbar">
          <button
            className="adm-topbar__burger"
            onClick={() => setSidebarOpen((o) => !o)}
            aria-label="Toggle menu"
          >
            <span /><span /><span />
          </button>
          <div className="adm-topbar__title">
            {TABS.find((x) => x.key === tab)?.icon}&nbsp;
            {TABS.find((x) => x.key === tab)?.label}
          </div>
          <div className="adm-topbar__right">
            <span className="adm-topbar__greeting">
              {user?.full_name?.split(' ')[0] || 'Admin'} 👋
            </span>
          </div>
        </header>

        <div className="adm-content">{renderContent()}</div>
      </main>

      {/* ═══ TOAST ═══ */}
      <div className={`adm-toast${toast.msg ? ' adm-toast--show' : ''} adm-toast--${toast.type}`}>
        {toast.msg}
      </div>

      {/* ═══ BOOKING GUEST PROFILE MODAL ═══ */}
      {selectedBooking && (
        <div className="adm-modal-overlay" onClick={() => setSelectedBooking(null)}>
          <div className="adm-modal" onClick={(e) => e.stopPropagation()}>
            <button className="adm-modal__close" onClick={() => setSelectedBooking(null)}>✕</button>

            <div className="adm-modal__header">
              <div className="adm-modal__avatar">
                {(selectedBooking.full_name?.[0] || selectedBooking.guest_name?.[0] || '?').toUpperCase()}
              </div>
              <div>
                <h2 className="adm-modal__title">
                  {selectedBooking.full_name || selectedBooking.guest_name || 'Guest'}
                </h2>
                <StatusPill status={selectedBooking.status} />
              </div>
            </div>

            <div className="adm-modal__body">
              <div className="adm-modal__section">
                <h4 className="adm-modal__section-title">📞 Contact Information</h4>
                <div className="adm-modal__fields">
                  <div className="adm-modal__field">
                    <span className="adm-modal__field-label">Full Name</span>
                    <span className="adm-modal__field-value">
                      {selectedBooking.full_name || selectedBooking.guest_name || '—'}
                    </span>
                  </div>
                  <div className="adm-modal__field">
                    <span className="adm-modal__field-label">Email</span>
                    <span className="adm-modal__field-value">
                      {selectedBooking.email
                        ? <a href={`mailto:${selectedBooking.email}`} className="adm-modal__link">{selectedBooking.email}</a>
                        : '—'}
                    </span>
                  </div>
                  <div className="adm-modal__field adm-modal__field--highlight">
                    <span className="adm-modal__field-label">Phone</span>
                    <span className="adm-modal__field-value">
                      {selectedBooking.phone
                        ? <a href={`tel:${selectedBooking.phone}`} className="adm-modal__link adm-modal__link--phone">
                            📞 {selectedBooking.phone}
                          </a>
                        : <span className="adm-modal__field-none">No phone on file</span>}
                    </span>
                  </div>
                </div>
              </div>

              <div className="adm-modal__section">
                <h4 className="adm-modal__section-title">📅 Booking Details</h4>
                <div className="adm-modal__fields">
                  <div className="adm-modal__field">
                    <span className="adm-modal__field-label">Booking ID</span>
                    <span className="adm-modal__field-value">#{selectedBooking.id}</span>
                  </div>
                  <div className="adm-modal__field">
                    <span className="adm-modal__field-label">Check-in</span>
                    <span className="adm-modal__field-value">{fmtDate(selectedBooking.check_in)}</span>
                  </div>
                  <div className="adm-modal__field">
                    <span className="adm-modal__field-label">Check-out</span>
                    <span className="adm-modal__field-value">{fmtDate(selectedBooking.check_out)}</span>
                  </div>
                  <div className="adm-modal__field">
                    <span className="adm-modal__field-label">Guests</span>
                    <span className="adm-modal__field-value">{selectedBooking.guests ?? '—'}</span>
                  </div>
                  <div className="adm-modal__field">
                    <span className="adm-modal__field-label">Booked on</span>
                    <span className="adm-modal__field-value">{fmtDateTime(selectedBooking.created_at)}</span>
                  </div>
                  {selectedBooking.notes && (
                    <div className="adm-modal__field adm-modal__field--full">
                      <span className="adm-modal__field-label">Notes</span>
                      <span className="adm-modal__field-value">{selectedBooking.notes}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Quick action buttons */}
            <div className="adm-modal__actions">
              {selectedBooking.status === 'pending' && (
                <>
                  <button
                    className="adm-btn adm-btn--success"
                    onClick={() => { handleBookingStatus(selectedBooking.id, 'confirmed'); setSelectedBooking(null); }}
                  >✅ Confirm Booking</button>
                  <button
                    className="adm-btn adm-btn--warn"
                    onClick={() => { handleBookingStatus(selectedBooking.id, 'cancelled'); setSelectedBooking(null); }}
                  >❌ Cancel</button>
                </>
              )}
              {selectedBooking.status === 'confirmed' && (
                <>
                  <button
                    className="adm-btn adm-btn--success"
                    onClick={() => { handleBookingStatus(selectedBooking.id, 'completed'); setSelectedBooking(null); }}
                  >🏁 Mark Completed</button>
                  <button
                    className="adm-btn adm-btn--warn"
                    onClick={() => { handleBookingStatus(selectedBooking.id, 'cancelled'); setSelectedBooking(null); }}
                  >❌ Cancel</button>
                </>
              )}
              <button className="adm-btn adm-btn--ghost" onClick={() => setSelectedBooking(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
